import { PopoverTrigger } from "@focal/ui-kit";
import { cn } from "@/lib/utils";
import { useAnalytics } from "@/lib";
import { ANALYTICS_EVENTS } from "@/constants";
import { routes } from "@/constants";
import AvatarComponent from "@/components/common/AvatarCustom/AvatarCustom";
import { Icon } from "@focal/ui-kit";
import { useRouter } from "next/navigation";
import { Participant } from "@/types";
import {
  getParticipantEmail,
  getParticipantName,
  getParticipantRoleLabel,
} from "@/lib/participant-utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@focal/ui-kit";
import { Tag } from "@focal/ui-kit";
import { Button } from "@focal/ui-kit";
import { Skeleton } from "@focal/ui-kit";

interface ConversationPersonCardProps {
  participant?: Partial<Participant>;
  onDeleteParticipant?: (participantId: string) => void;
  disableRedirect?: boolean;
  disableRemove?: boolean;
  disableUsers?: boolean;
  isLoading?: boolean;
  showAssignmentStatus?: boolean;
}

export const ConversationPersonCard = ({
  participant,
  onDeleteParticipant,
  disableRedirect,
  disableRemove,
  disableUsers,
  isLoading = false,
  showAssignmentStatus = false,
}: ConversationPersonCardProps) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const participantName = getParticipantName(participant);
  const participantEmail = getParticipantEmail(participant);

  if (
    (participantName || participantEmail) &&
    !participant?.clientId &&
    !participant?.userId
  ) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "flex items-center cursor-pointer space-x-2 p-2 m-0 h-[68px] min-w-[240px] border border-dashed border-border rounded-xl hover:border-solid",
              )}
            >
              {isLoading ? (
                <div>
                  <Skeleton className="w-[216px] h-10" />
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <h3 className="text-base truncate min-w-0 transition-all pr-10">
                    Click to add
                  </h3>
                  <p className="text-sm text-typography-secondary truncate min-w-0">
                    {participantName || participantEmail || ""}
                  </p>
                </div>
              )}
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p className="whitespace-pre-wrap max-w-[220px] text-center">
            Information detected — add a participant
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (participant) {
    return (
      <>
        <div
          className={cn(
            "flex space-x-2 p-2 m-0",
            participant?.clientId && "cursor-pointer group/link",
            showAssignmentStatus ? "items-center min-h-[68px]" : "items-center",
          )}
          onClick={() => {
            if (participant?.clientId && !disableRedirect) {
              router.push(routes.clients.details(participant?.clientId));
            }
          }}
        >
          {isLoading ? (
            <div>
              <Skeleton className="w-[216px] h-10" />
            </div>
          ) : (
            <>
              <AvatarComponent
                name={participantName}
                className="group-hover/link:scale-105 transition-all w-6 h-6 rounded-full text-xs shrink-0"
              />
              <div className="pr-6">
                <div className="w-full overflow-hidden max-w-[200px]">
                  <h3 className="text-base truncate min-w-0 transition-all pr-10">
                    {participantName}
                  </h3>
                  <p
                    className={cn(
                      "text-sm text-typography-secondary truncate min-w-0",
                      showAssignmentStatus && "mt-1",
                    )}
                  >
                    {participantEmail}
                  </p>
                </div>
                {(participant?.userId ||
                  participant?.clientId ||
                  showAssignmentStatus) && (
                  <div className="absolute top-0.5 right-2 text-xs flex flex-col items-end gap-1">
                    {(participant?.userId || participant?.clientId) && (
                      <Tag variant="blue" size="xs">
                        {getParticipantRoleLabel(participant)}
                      </Tag>
                    )}
                    {showAssignmentStatus && (
                      <Tag
                        variant={participant?.speakerId ? "green" : "orange"}
                        size="xs"
                      >
                        {participant?.speakerId ? "Assigned" : "Unassigned"}
                      </Tag>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!disableRemove && (
          <div className="flex absolute right-[-100px] px-4 top-0 h-full group-hover:right-0 justify-center transition-all items-center bg-[#F1F5F9] gap-2 shadow-[rgba(245,245,245,0.7)_0_5px_4px_20px]">
            <Icon
              name="CloseIcon"
              size="xl"
              className="p-1 cursor-pointer rounded hover:bg-black/10 transition-all"
              data-cy="delete-participant"
              onClick={(e) => {
                e.stopPropagation();
                if (participant.id && onDeleteParticipant) {
                  track(
                    ANALYTICS_EVENTS.CONVERSATION_DETAILS_PARTICIPANT_REMOVED,
                    {
                      event_text: "Remove participant",
                    },
                  );
                  onDeleteParticipant(participant.id);
                }
              }}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>
          {disableUsers ? (
            <Button
              iconLeft={<Icon name="AddIcon" />}
              variant="ghost"
              data-cy="add-participant-button"
            >
              Add participant
            </Button>
          ) : (
            <div
              className="flex flex-1 justify-center items-center font-bold py-2 px-2"
              data-cy="add-participant-button"
            >
              <Icon name="AddIcon" size="lg" />
            </div>
          )}
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p className="whitespace-pre-wrap max-w-[220px] text-center">
          {disableUsers ? "Add a client" : "Add a client or advisor"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};
